
import React from 'react';
import { ParcelDetails } from "../ParcelBookingForm";
import { ParcelValidationErrors } from "@/hooks/useParcelValidation";
import { useParcelTypes } from "@/hooks/useParcelTypes";
import ParcelTypeSelector from "./ParcelTypeSelector";
import ParcelFormHeader from "./components/ParcelFormHeader";
import ParcelDescriptionField from "./components/ParcelDescriptionField";
import ParcelDimensionsFields from "./components/ParcelDimensionsFields";
import ParcelInsuranceField from "./components/ParcelInsuranceField";
import ParcelQuantityField from "./components/ParcelQuantityField";
import OtherTypeSpecificationField from "./components/OtherTypeSpecificationField";

interface ParcelFormProps {
  parcel: ParcelDetails;
  index: number;
  onUpdate: (index: number, field: string, value: any) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  errors?: ParcelValidationErrors;
}

const ParcelForm: React.FC<ParcelFormProps> = ({
  parcel,
  index,
  onUpdate,
  onRemove,
  canRemove,
  errors = {}
}) => {
  const { parcelTypes } = useParcelTypes();

  const handleParcelTypeChange = (value: string) => {
    const selectedType = parcelTypes.find(type => type.key === value);
    onUpdate(index, 'type', value);
    onUpdate(index, 'otherTypeSpecification', value === "other" ? parcel.otherTypeSpecification : "");

    // Apply default values if they exist
    if (selectedType?.default_weight) {
      onUpdate(index, 'weight', selectedType.default_weight);
    }
    
    if (selectedType?.default_dimensions) {
      onUpdate(index, 'dimensions', {
        ...selectedType.default_dimensions
      });
    }
  };

  const handleInsuranceToggle = (enabled: boolean) => {
    onUpdate(index, 'insuranceEnabled', enabled);
    if (!enabled) {
      onUpdate(index, 'value', '');
    }
  };

  const handleDimensionChange = (field: string, value: string) => {
    onUpdate(index, `dimensions.${field}`, value);
  };

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <ParcelFormHeader
        index={index}
        canRemove={canRemove}
        onRemove={onRemove}
      />

      {/* Parcel Type Selection */}
      <ParcelTypeSelector
        selectedType={parcel.type}
        onTypeChange={handleParcelTypeChange}
        errors={errors}
      />

      {parcel.type === "other" && (
        <OtherTypeSpecificationField
          value={parcel.otherTypeSpecification}
          onChange={(value) => onUpdate(index, 'otherTypeSpecification', value)}
          errors={errors}
        />
      )}

      {/* Description */}
      <ParcelDescriptionField
        description={parcel.description}
        onDescriptionChange={(value) => onUpdate(index, 'description', value)}
      />

      {/* Dimensions and Weight */}
      <ParcelDimensionsFields
        dimensions={parcel.dimensions}
        weight={parcel.weight}
        onDimensionChange={handleDimensionChange}
        onWeightChange={(value) => onUpdate(index, 'weight', value)}
        errors={errors}
      />

      {/* Insurance Toggle and Value */}
      <ParcelInsuranceField
        insuranceEnabled={parcel.insuranceEnabled}
        value={parcel.value}
        index={index}
        onInsuranceToggle={handleInsuranceToggle}
        onValueChange={(value) => onUpdate(index, 'value', value)}
        errors={errors}
      />

      {/* Quantity */}
      <ParcelQuantityField
        quantity={parcel.quantity}
        onQuantityChange={(value) => onUpdate(index, 'quantity', value)}
      />
    </div>
  );
};

export default ParcelForm;
